/* eslint-disable react/no-unstable-nested-components */
import {
    Button,
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { Link, useSearchParams } from "react-router-dom";
import { INoteAuditReportRow } from "../../types/reports.types";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { useGetNoteAuditReport } from "../../hooks/queries/reports.queries";
import { useDebounce } from "../../../../hooks/helpers";
import { ProviderCredential } from "../../../clients/types/clients.types";
import { IAppCustomDates } from "../../../../utils/types";
import ChevronUpIcon from "../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import { NoteStatus } from "../../../notes/types/notes.types";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";
import NoteAuditReportProviderBreakdown from "../NoteAuditReportProviderBreakdown/NoteAuditReportProviderBreakdown";

interface ISubComponentBreakdown {
    // eslint-disable-next-line react/no-unused-prop-types
    row: Row<INoteAuditReportRow>;
}

export default function NoteAuditReportList() {
    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});

    const [searchParams, setSearchParams] = useSearchParams();

    const providerNameFilter = searchParams.get("provider_name") || "";
    const providerCredentialFilter =
        searchParams.get("provider_credential") || "";
    const customDateFilter = searchParams.get("custom_date") || "this_month";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const fromDateCreatedAtFilter = searchParams.get("from_created_at_date");
    const toDateCreatedAtFilter = searchParams.get("to_created_at_date");
    const includeNonBillableNotesFilter =
        searchParams.get("include_non_billable_notes") || "";

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const fromDateCreatedAtFilterToDate = convertDateFilterStringToDate(
        fromDateCreatedAtFilter
    );
    const toDateCreatedAtFilterToDate = convertDateFilterStringToDate(
        toDateCreatedAtFilter
    );

    const name = useDebounce(providerNameFilter, 500);

    const { data, isLoading, error, isSuccess } = useGetNoteAuditReport({
        provider_name: name,
        provider_credential: providerCredentialFilter,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        custom_date: customDateFilter,
        from_created_at_date: fromDateCreatedAtFilter,
        to_created_at_date: toDateCreatedAtFilter,
        include_non_billable_notes: includeNonBillableNotesFilter,
    });

    const credentialOptionsForSelect = Object.values(ProviderCredential).map(
        (credential) => {
            return credential;
        }
    );

    const customDateForSelect = Object.values(IAppCustomDates).map((item) => {
        return item;
    });

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("provider_name");
        else searchParams.set("provider_name", e.target.value);

        setSearchParams(searchParams);
    };

    const handleCredentialChange = (value: string) => {
        if (value === "") searchParams.delete("provider_credential");
        else searchParams.set("provider_credential", value);
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleCustomDateChange = (value: IAppCustomDates) => {
        searchParams.set("custom_date", value);
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleFromDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_created_at_date");
        else
            searchParams.set(
                "from_created_at_date",
                format(date, "yyyy-MM-dd")
            );
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleToDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_created_at_date");
        else searchParams.set("to_created_at_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleIncludeNonBillableNotesFilterChange = (value: string) => {
        searchParams.set("include_non_billable_notes", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const getIncludeNonBillableNotesFilterTextToDisplay = () => {
        if (includeNonBillableNotesFilter === "true") {
            return "Include non-billable notes";
        }
        return "Show only billable notes";
    };

    const renderRowSubComponent = useCallback(
        ({ row }: ISubComponentBreakdown) => (
            <NoteAuditReportProviderBreakdown
                row={row.original}
                isExpanded={row.getIsExpanded()}
            />
        ),
        []
    );

    const getErrorAndLoadingStates = () => {
        if (error) {
            return "0";
        }

        if (isLoading) {
            return <Loader loaderType="inline" />;
        }

        return "";
    };

    const columns: ColumnDef<INoteAuditReportRow>[] = useMemo(() => {
        return [
            {
                accessorKey: "first_name",
                header: "Provider name",
                cell: ({ row }) => (
                    <div className="flex gap-[--spacing-items]">
                        <Button
                            variant="normal"
                            size="auto"
                            onClick={() => {
                                row.toggleExpanded();
                                row.getToggleExpandedHandler();
                            }}
                            className="size-4 [border:1px_solid_var(--color-text)] rounded-full"
                        >
                            {row.getIsExpanded() ? (
                                <ChevronUpIcon />
                            ) : (
                                <ChevronDownIcon />
                            )}
                        </Button>

                        <Link
                            to={`/providers/${row.original.provider_id}?tab=provider-caseload`}
                            className="underline"
                        >
                            {displayNameInRightFormat({
                                firstName: row.original.first_name,
                                lastName: row.original.last_name,
                            })}
                        </Link>
                    </div>
                ),
            },
            {
                accessorKey: "supervisor_first_name",
                header: "SUPERVISORS NAME",
                cell: ({ row }) =>
                    row.original.supervisor_first_name ? (
                        <Link
                            to={`/providers/${row.original.supervisor_provider_id}?tab=Supervisees`}
                            className="underline"
                        >
                            {displayNameInRightFormat({
                                firstName: row.original.supervisor_first_name,
                                lastName: row.original.supervisor_last_name,
                            })}
                        </Link>
                    ) : (
                        "--"
                    ),
            },
            {
                accessorKey: "no_of_clients",
                header: "NO OF CLIENTS",
            },
            {
                accessorKey: "notes_completed",
                header: "NOTES COMPLETED",
                cell: ({ row }) => {
                    const providerName = `${row.original.first_name} ${row.original.last_name}`;
                    return (
                        <Link
                            to={`/notes?provider_name=${providerName}&status%5B%5D=${NoteStatus.CODED}&status%5B%5D=${NoteStatus.PENDING_CODE_REVIEW}`}
                            className="underline"
                        >
                            {row.original.notes_completed}
                        </Link>
                    );
                },
            },
            {
                accessorKey: "late_notes",
                header: "Late notes",
                cell: ({ row }) => row.original.late_notes,
            },
            {
                accessorKey: "no_of_changes_requested",
                header: "Changes requested",
                cell: ({ row }) => row.original.no_of_changes_requested,
            },
            {
                accessorKey: "notes_pending_review",
                header: "NOTES PENDING REVIEW",
                cell: ({ row }) => {
                    const providerName = `${row.original.first_name} ${row.original.last_name}`;
                    return (
                        <Link
                            to={`/notes?provider_name=${providerName}&status%5B%5D=${NoteStatus.PENDING_SUPERVISOR_REVIEW}`}
                            className="underline"
                        >
                            {row.original.notes_pending_review}
                        </Link>
                    );
                },
            },
        ];
    }, []);

    return (
        <div className="page">
            <Card classNames="mb-[--spacing-header]">
                <div className="flex flex-col gap-y-[--spacing-items]">
                    <SearchInput
                        placeholder="Search by provider"
                        containerClass="w-[400px]"
                        onChange={handleSetSearchString}
                        defaultValue={providerNameFilter}
                    />
                    <div className="flex items-center gap-[--spacing-items] justify-between flex-wrap">
                        <div className="flex gap-[--spacing-items] flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {providerCredentialFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      providerCredentialFilter
                                                  )
                                              )
                                            : "Credential"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width={100}>
                                    <DropdownItem
                                        isSelected={!providerCredentialFilter}
                                        onClick={() =>
                                            handleCredentialChange("")
                                        }
                                    >
                                        All
                                    </DropdownItem>
                                    {credentialOptionsForSelect.map(
                                        (credential) => (
                                            <DropdownItem
                                                isSelected={
                                                    providerCredentialFilter ===
                                                    credential
                                                }
                                                key={credential}
                                                onClick={() =>
                                                    handleCredentialChange(
                                                        credential
                                                    )
                                                }
                                            >
                                                {makeStringFirstLetterCapital(
                                                    credential
                                                )}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {customDateFilter
                                            ? truncateString(
                                                  makeStringFirstLetterCapital(
                                                      removeEnumUnderscore(
                                                          customDateFilter
                                                      )
                                                  )
                                              )
                                            : "Date range"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="start" width={150}>
                                    {customDateForSelect.map((range) => (
                                        <DropdownItem
                                            isSelected={
                                                customDateFilter === range
                                            }
                                            key={range}
                                            onClick={() =>
                                                handleCustomDateChange(range)
                                            }
                                        >
                                            {removeEnumUnderscore(range)}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date Of Service</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            className="hover:border-primary"
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        className="hover:border-primary"
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date Created</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={
                                                fromDateCreatedAtFilterToDate
                                            }
                                            onChange={
                                                handleFromDateCreatedFilterChange
                                            }
                                            maxDate={
                                                toDateCreatedAtFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateCreatedAtFilterToDate}
                                        onChange={
                                            handleToDateCreatedFilterChange
                                        }
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {getIncludeNonBillableNotesFilterTextToDisplay()}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width={220}>
                                    <DropdownItem
                                        isSelected={
                                            includeNonBillableNotesFilter ===
                                            "false"
                                        }
                                        onClick={() =>
                                            handleIncludeNonBillableNotesFilterChange(
                                                "false"
                                            )
                                        }
                                    >
                                        Show only billable notes
                                    </DropdownItem>
                                    <DropdownItem
                                        isSelected={
                                            includeNonBillableNotesFilter ===
                                            "true"
                                        }
                                        onClick={() =>
                                            handleIncludeNonBillableNotesFilterChange(
                                                "true"
                                            )
                                        }
                                    >
                                        Include non-billable notes
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </Card>
            <Card classNames="grid grid-cols-[repeat(3,minmax(209px,1fr))] overflow-x-auto overflow-y-hidden gap-x-[--spacing-items] mb-[--spacing-header]">
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Notes Completed
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() ||
                            data?.data.summary.total_notes_completed}
                    </div>
                </Card>
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Lates Notes
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() ||
                            data?.data.summary.total_late_notes}
                    </div>
                </Card>
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Number of changes requested
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() ||
                            data?.data.summary.total_no_of_changes_requested}
                    </div>
                </Card>
            </Card>
            {isLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display provider session activity list at this time please try again later`
                    }
                />
            )}
            {data && isSuccess && data.data && data.data.rows.length === 0 && (
                <ListState
                    stateHelperText="Provider session activity list will appear here once providers are added"
                    isError={false}
                    emptyMessage="No providers session activity list added yet"
                />
            )}
            {data && isSuccess && data.data && data.data.rows.length > 0 && (
                <Table
                    columns={columns}
                    data={data.data.rows}
                    isHeaderFixed
                    expandedRows={expandedRows}
                    setExpandedRows={setExpandedRows}
                    renderRowSubComponent={renderRowSubComponent}
                />
            )}
        </div>
    );
}
